import { ReactElement, useEffect } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import type { IBaseModel, IListFetchParams } from '../../common';
import { useListContext } from '../../common';
import { useCrud } from '../../common';
import CrudTitle from './CrudTitle/index';
import CrudButtons from './CrudButtons/index';
import CrudBody from './CrudBody/index';
import { AxiosError } from 'axios';
import { handleAxiosError } from '../../utils';
import React from 'react';
import { Alert, Icon, Loader } from '@jobcost-monorepo/ui';
import { useTranslation } from '@jobcost-monorepo/i18n';

const CrudForm = <
  T extends IBaseModel,
  Q extends IListFetchParams,
>(): ReactElement => {
  const { t } = useTranslation();
  const [isEdit, setEdit] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [defaultValues, setDefaultValues] = React.useState<T | undefined>();
  const {
    fetchDataList,
    params,
    setFormOpen,
    setFormElement,
    formElement,
    moduleName,
    repository,
    updateStore,
  } = useListContext<T, Q>();
  const { methods, fetchDataElement, loading, setloading } = useCrud<T, Q>({
    moduleName,
    repository,
    updateStore,
    id: formElement?.id,
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
  } = methods;
  console.log(errors);

  useEffect(() => {
    if (isEdit) {
      formElement?.id && fetchDataElement();
      return;
    }
    setDefaultValues(methods.getValues());
  }, [isEdit]);

  useEffect(() => {
    setEdit(!!formElement?.id);
  }, []);

  const onSubmit: SubmitHandler<T> = async (data) => {
    setloading(true);
    await repository
      .createOrUpdate(formElement?.id, data)
      .then(() => (isEdit ? handleCloseFormAndFetch() : handleSaveAndNew()))
      .catch((err: AxiosError) => handleAxiosError(err));
    setloading(false);
  };

  const handleCloseFormAndFetch = async () => {
    await fetchDataList({
      ...params,
    } as Q);
    await handleCloseForm();
  };

  const handleCloseForm = async () => {
    reset();
    if (setFormElement) {
      setFormElement(undefined);
    }
    if (!isEdit) {
      await fetchDataList({
        ...params,
      } as Q);
    }
    setFormOpen(false);
  };

  const handleSaveAndNew = () => {
    setShowPopup(true);
    reset(defaultValues);
  };

  useEffect(() => {
    if (showPopup) {
      setTimeout(() => {
        setShowPopup(false);
      }, 3000);
    }
  }, [showPopup]);

  useEffect(() => {
    if (!formElement?.id) setFormElement(undefined);
  }, [formElement?.id]);

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="size-full flex flex-col max-h-max"
        >
          <div className="flex justify-between items-center content-center">
            <CrudTitle moduleName={moduleName} />
            <div
              onClick={handleCloseForm}
              className="bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 rounded-2xl p-1"
            >
              <Icon
                iconName="cancel"
                className="hover:opacity-50 hover:cursor-pointer fill-black stroke-black dark:fill-white dark:stroke-white"
              />
            </div>
          </div>

          <div className="flex flex-grow overflow-hidden flex-col border h-full justify-between py-4 mt-4 rounded-lg border-purple70">
            <Loader showLoader={loading} />
            <CrudBody repository={repository} />
            <div>
              <CrudButtons handleCloseForm={handleCloseForm} />
            </div>
          </div>
          {repository.hook && <repository.hook />}
        </form>
      </FormProvider>
      {showPopup && <Alert type="success" message={t('recordSaved')} />}
    </>
  );
};

export default CrudForm;
